import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  DialogActions,
  Divider,
  makeStyles,
  Typography,
} from '@material-ui/core'
import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslate } from 'react-admin'
import LoaderDots from '../../components/loaders/LoaderDots/LoaderDots'
import orgUsersPageStyles from '../OrgUsers/OrgUsersPage/orgUsersPage.styles'
import MyTable from '../../components/DynamicTable/MyTable/MyTable'
import UserField from '../../components/DynamicTable/Fields/UserField/UserField'
import TypographyField from '../../components/DynamicTable/Fields/TypographyField/TypographyField'
import AcceptRejectField from '../../components/DynamicTable/Fields/AcceptRejectField/AcceptRejectField'
import QueryKeys from '../../utils/constants/queryKeys'
import invitationAccept from '../../Apis/invitations/invitationAccept'
import invitationReject from '../../Apis/invitations/invitationReject'
import getUserInvitations from '../../Apis/invitations/getUserInvitations'
import DialogWithCloseIcon from '../../components/DialogWithCloseIcon/DialogWithCloseIcon'
import ButtonWithLoader from '../../components/ui/ButtonWithLoader/ButtonWithLoader'
import MySnackbar from '../../components/MySnackbar/MySnackbar'
import localStorageIds from '../../utils/constants/localStorageIds'
import CardWrapperMoreIcon from '../../components/layout/CardWrapperMoreIcon/CardWrapperMoreIcon'
import invitationsListStyles from './invitationsList.styles'
import { setLocalStorageValue } from '../../utils/helpers/localStorageHelperMethods'
import storableOrg from '../../utils/helpers/storableOrg'
import getOrgs from '../../Apis/organisation/getOrgs'
import useHistoryPush from '../../hooks/useHistoryPush'
import INVITATION_TYPES from '../../utils/constants/invitationTypes'
import getOrgContract from '../../Apis/contract/getOrgContract'
import ContractSigningDialog from '../../components/ContractSigningDialog/ContractSigningDialog'
import TextButton from '../../components/ui/TextButton/TextButton'
import DefaultPlayerIcon from '../../assets/icons/defaultPlayerIcon'
import useIsMobileScreen from '../../hooks/useIsMobileScreen'
import useGetLocalStorage from '../../hooks/useGetLocalStorage'
import getQueryParameterByName from '../../utils/helpers/getQueryParameterByName'

const CLOSED_CONTRACT_SIGNING = {
  isOpen: false,
  invitationId: '',
  orgId: '',
  contract: null,
  errorMessage: '',
}

const serverMessage = (error) => {
  const data = error?.response?.data
  return data?.details || data?.message || ''
}

const InvitationsList = () => {
  const [invitations, setInvitations] = useState([])
  // The outcome is recorded when the snackbar opens: react-query keeps `isSuccess` true until the
  // mutation is reset, so a failed accept after an earlier successful one would otherwise render green.
  const [snackbar, setSnackbar] = useState({ isOpen: false, isSuccess: false, message: '' })
  const [deleteInvitationModal, setDeleteInvitationModal] = useState({
    isOpen: false,
    senderName: '',
    id: '',
    orgName: '',
  })
  const [contractSigning, setContractSigning] = useState(CLOSED_CONTRACT_SIGNING)
  const queryClient = useQueryClient()
  const translate = useTranslate()
  const { redirect } = useHistoryPush()
  const useStyles = makeStyles(invitationsListStyles)
  const classes = useStyles()
  const isMobileScreen = useIsMobileScreen()
  const selectedOrg = useGetLocalStorage()?.[localStorageIds.SELECTED_ORG]

  const showSuccess = (message) => setSnackbar({ isOpen: true, isSuccess: true, message })
  const showError = (message) => setSnackbar({ isOpen: true, isSuccess: false, message })
  const closeContractScreen = () => setContractSigning(CLOSED_CONTRACT_SIGNING)

  const updateAfterInvitationAccept = (token) => {
    setLocalStorageValue([{ id: localStorageIds.TOKEN, value: token }])

    queryClient.invalidateQueries(QueryKeys.GET_INVITATIONS).then(() => {})
    queryClient.invalidateQueries(QueryKeys.GET_ACCOUNT_ME).then(() => {
      queryClient.invalidateQueries(QueryKeys.GET_ORGS).then(() => {
        queryClient.invalidateQueries(QueryKeys.GET_ORGS_AFTER_LOGIN).then(() => {
          queryClient.invalidateQueries(QueryKeys.GET_TEAMS_AFTER_LOGIN).then(() => {})
        })
      })
    })

    showSuccess(translate('ra.successMessages.invitationAccepted'))
  }

  // A kick-off makes the signer the owner of an org they had no access to a moment ago; land them
  // in it rather than on an emptied invitation list. SetUrlAfterLogin honours the ?org= param.
  const enterAcceptedOrg = async (orgId) => {
    try {
      const orgs = await getOrgs()
      const org = orgs?.find((o) => o.id === orgId)
      if (org) setLocalStorageValue([{ id: localStorageIds.SELECTED_ORG, value: storableOrg(org) }])
    } catch {
      // The list still works from the stored org; SetUrlAfterLogin will re-select on the next load.
    }

    redirect('/calendar', [{ org: orgId }])
  }

  // The invitation an email's link named comes first.
  const linkedInvitationId = getQueryParameterByName('invitation')

  const memberInvitations = useQuery(QueryKeys.GET_INVITATIONS, () => getUserInvitations(), {
    onSuccess: (response) => {
      const rows = (response.data ?? []).map((inv) => ({
        ...inv,
        name: `${inv.sender.firstName ?? ''} ${inv.sender.lastName ?? ''}`.trim(),
        orgName: inv.org.name,
        orgLogo: inv.org.logoUrl,
      }))

      setInvitations([
        ...rows.filter((row) => row.id === linkedInvitationId),
        ...rows.filter((row) => row.id !== linkedInvitationId),
      ])
    },
  })

  // The dialog goes up immediately with a loader, so the Accept click is visibly answered while the PDF loads.
  const openContractScreen = async (invitationId, orgId, type) => {
    setContractSigning({ isOpen: true, invitationId, orgId, type, contract: null, errorMessage: '' })

    try {
      const contract = await getOrgContract(orgId)
      setContractSigning({ isOpen: true, invitationId, orgId, type, contract, errorMessage: '' })
    } catch {
      // Already reported by the interceptor; a spinner that never fills would strand the signer.
      closeContractScreen()
    }
  }

  // A rejected acceptance may mean the contract was re-issued while the signer was reading it. If so,
  // keep the screen up with the new document instead of bouncing back to the list.
  const refreshAfterDocumentChange = async ({ invitationId, orgId, documentHash }) => {
    let contract
    try {
      contract = await getOrgContract(orgId, { skipErrorNotification: true })
    } catch {
      return false
    }

    if (!contract || contract.documentHash === documentHash) return false

    setContractSigning({
      isOpen: true,
      invitationId,
      orgId,
      contract,
      errorMessage: translate('ra.kickoffContract.versionChanged'),
    })

    return true
  }

  const acceptInvitation = useMutation(
    QueryKeys.INVITATION_ACCEPT,
    (data) => invitationAccept(data),
    {
      onSuccess: (res, variables) => {
        closeContractScreen()
        updateAfterInvitationAccept(res?.accessToken)
        if (variables.type === INVITATION_TYPES.KICKOFF) enterAcceptedOrg(variables.orgId)
      },
      onError: async (error, variables) => {
        if (variables.documentHash && (await refreshAfterDocumentChange(variables))) return

        closeContractScreen()
        if (variables.documentHash) {
          showError(serverMessage(error) || translate('ra.errorMessages.errorMessages'))
        }
      },
    },
  )

  const rejectInvitation = useMutation(
    QueryKeys.INVITATION_REJECT,
    (data) => invitationReject(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKeys.GET_INVITATIONS).then(() => {})
        showSuccess(translate('ra.errorMessages.invitationRejected'))
      },
    },
  )

  const onAcceptClick = (e, row) => {
    e.preventDefault()
    e.stopPropagation()

    if (row.contractRequired) {
      openContractScreen(row.id, row.orgId, row.type)
      return
    }

    acceptInvitation.mutate({ orgId: row.orgId, invitationId: row.id, type: row.type })
  }

  const onContractAccept = (documentHash) => {
    setContractSigning((previous) => ({ ...previous, errorMessage: '' }))
    acceptInvitation.mutate({
      invitationId: contractSigning.invitationId,
      orgId: contractSigning.orgId,
      type: contractSigning.type,
      documentHash,
    })
  }
  const onRejectClick = (e, row) => {
    e.preventDefault()
    e.stopPropagation()
    setDeleteInvitationModal({
      isOpen: true,
      senderName: row.name,
      id: row.id,
      orgId: row.orgId,
      orgName: row.orgName,
    })
  }

  return (
    <>
      <ContractSigningDialog
        open={contractSigning.isOpen}
        contract={contractSigning.contract}
        accepting={acceptInvitation.isLoading}
        errorMessage={contractSigning.errorMessage}
        onAccept={onContractAccept}
      />

      <DialogWithCloseIcon
        open={deleteInvitationModal.isOpen}
        onClose={() => setDeleteInvitationModal({ ...deleteInvitationModal, isOpen: false })}
        dialogTitle={
          <Typography style={{ width: '95%' }} className={classes.cardTitle}>
            {`${translate('ra.text.deleteInvitationConfirmation')} ${
              deleteInvitationModal.senderName
            } to join ${deleteInvitationModal.orgName}?
            `}
          </Typography>
        }
        dialogWidth={isMobileScreen ? 'calc(100% - 32px)' : '40%'}
      >
        <DialogActions>
          <Button
            variant='text'
            classes={{ root: classes.cancelButton }}
            onClick={() => setDeleteInvitationModal({ ...deleteInvitationModal, isOpen: false })}
          >
            {translate('ra.buttons.cancel')}
          </Button>
          <ButtonWithLoader
            label={translate('ra.buttons.reject')}
            className={classes.deleteButton}
            onClick={() => {
              rejectInvitation.mutate({
                orgId: deleteInvitationModal.orgId,
                invitationId: deleteInvitationModal.id,
              })
              setDeleteInvitationModal({ ...deleteInvitationModal, isOpen: false })
            }}
            loading={rejectInvitation.isLoading}
          />
        </DialogActions>
      </DialogWithCloseIcon>

      <CardWrapperMoreIcon>
        <Card className={classes.card}>
          <CardHeader
            title={
              <Typography className={classes.cardTitle} variant='h5'>
                {translate('ra.text.invitations')}
              </Typography>
            }
            subheader={
              <Typography className={classes.cardSubtitle}>
                {translate('ra.text.invitationsList')}
              </Typography>
            }
          />
          <CardContent>
            <Divider variant='middle' className={classes.divider} />
            {memberInvitations.isLoading ? (
              <LoaderDots style={orgUsersPageStyles.loaderIcon} />
            ) : !invitations?.length ? (
              // Accepting the last invitation used to leave an empty table: point the way into the org.
              <div className={classes.empty}>
                <Typography className={classes.emptyText}>
                  {translate('ra.text.noPendingInvitations')}
                </Typography>
                {selectedOrg?.id && (
                  <ButtonWithLoader
                    type='button'
                    label={translate('ra.buttons.goToCalendar')}
                    onClick={() => redirect('/calendar', [{ org: selectedOrg.id }])}
                  />
                )}
              </div>
            ) : isMobileScreen ? (
              // Four columns don't fit a phone: one card per invitation, the answer under it.
              invitations.map((row) => (
                <div key={row.id} className={classes.mobileInvitation}>
                  <div className={classes.mobileOrg}>
                    {row.orgLogo ? (
                      <Avatar alt='avatar' src={row.orgLogo} className={classes.mobileOrgLogo} />
                    ) : (
                      <DefaultPlayerIcon />
                    )}
                    <Typography className={classes.mobileOrgName}>{row.orgName}</Typography>
                  </div>
                  <Typography className={classes.mobileDetails}>
                    {`${row.role} · ${translate('ra.text.invitedBy', { name: row.name })}`}
                  </Typography>
                  <div className={classes.mobileActions}>
                    <TextButton
                      label={translate('ra.buttons.reject')}
                      onClick={(e) => onRejectClick(e, row)}
                    />
                    <ButtonWithLoader
                      label={translate('ra.buttons.accept')}
                      onClick={(e) => onAcceptClick(e, row)}
                    />
                  </div>
                </div>
              ))
            ) : (
              <MyTable rows={invitations} hasHeader hasDivider size='small' boldHeader>
                <TypographyField title='Sender' key='name' colorChoice='black' />
                <UserField title='Organisation' key='orgName' logoName='orgLogo' />
                <TypographyField title='Role' key='role' colorChoice='black' />
                <AcceptRejectField
                  title=''
                  onAcceptClick={onAcceptClick}
                  onRejectClick={onRejectClick}
                  columnAlignment='center'
                />
              </MyTable>
            )}
          </CardContent>
        </Card>
      </CardWrapperMoreIcon>
      <MySnackbar
        isOpen={snackbar.isOpen}
        onClose={() => setSnackbar((previous) => ({ ...previous, isOpen: false }))}
        isSuccess={snackbar.isSuccess}
        successMessage={snackbar.message}
        errorMessage={snackbar.message}
      />
    </>
  )
}

export default InvitationsList
